import { useRootStore } from '@Shared/stores/root';
import baseAxios from 'axios';
import { storeToRefs } from 'pinia';
import { useAppInsights } from '@Shared/utils/util';

baseAxios.interceptors.request.use((config) => {
  const rootStore = useRootStore();
  const { token, currentOutstandingRequests, portalUri } = storeToRefs(rootStore);
  if (!(portalUri.value && config.url)) return config;

  currentOutstandingRequests.value += 1;

  // add headers for portal api calls
  // .origin to remove trailing slashes
  if (config.url.startsWith(`${new URL(portalUri.value).origin}/api`)) {
    config.headers.Authorization = `Bearer ${token.value?.access_token ?? ''}`;
    config.headers['Content-Type'] = 'application/json';
    config.headers['Access-Control-Allow-Origin'] = '*';
  }

  return config;
});

baseAxios.interceptors.response.use(
  (response) => {
    const rootStore = useRootStore();
    const { currentOutstandingRequests } = storeToRefs(rootStore);
    currentOutstandingRequests.value -= 1;
    return response;
  },
  async (error) => {
    // Log error to Application Insights
    const appInsights = useAppInsights();
    if (appInsights && typeof appInsights.trackException === 'function') {
      appInsights.trackException({ exception: error });
    }

    const rootStore = useRootStore();
    const { token, currentOutstandingRequests } = storeToRefs(rootStore);
    currentOutstandingRequests.value -= 1;

    const { response } = error;
    if (response) {
      // if it returns between 500 - 600, and is not a visual break, push to error page
      if (response.status >= 500 && response.status < 600 && !response.config.url.includes('dashboard/visuals')) {
        window.location.assign('/error');
      } else if (response.status === 401 || response.status === 403) {
        // if the response body contains NoPermission, push to the 401 no permission page
        if (response.data?.error === 'NoPermission') {
          window.location.assign('/401');
        } else {
          if (!response.config._retryCount) {
            response.config._retryCount = 0;
          }

          response.config._retryCount += 1;
          if (response.config._retryCount <= 1) {
            // otherwise repeat it again
            const refreshedToken = token.value?.access_token;
            if (refreshedToken) {
              response.config.headers.Authorization = `Bearer ${refreshedToken}`;
              return await baseAxios(response.config);
            } else {
              // if there is no token, redirect to the login page
              window.location.assign('/login');
            }
          } else {
            // If the retry limit is reached, redirect to /403
            window.location.assign('/403');
          }
        }
      }
    } else {
      window.location.assign('/error');
    }

    return Promise.reject(error);
  }
);

export const axios = baseAxios;
